// Document Studio (Catalog Publishing) API client + TanStack Query hooks.
// Mirrors backend app/api/v1/document_studio.py. Manual-fetch style (like
// RealDraftListingPage) — the global authFetch wrapper attaches the bearer token
// in real-engine mode; MSW intercepts in maket mode.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiUrl } from '../lib/api'

export type ExportType = 'full' | 'category' | 'selected_products'
export type ExportStatus = 'queued' | 'running' | 'completed' | 'failed'
export type PriceTier = 'internal' | 'partner' | 'reseller' | 'b2b'

export interface CatalogExportFilters {
  category_keys: string[]
  subcategory_ids: string[]
  price_visible: boolean
  price_tier: PriceTier
  include_intro: boolean
  include_coming_soon: boolean
}

export interface CatalogExportCreate {
  export_type: ExportType
  title?: string | null
  filters: CatalogExportFilters
  selected_product_ids: string[]
  brand_profile_id?: string | null
  template_version?: string
}

export interface CatalogExportRun {
  id: string
  export_type: ExportType
  title: string | null
  status: ExportStatus
  product_count: number
  template_version: string
  brand_profile_id: string | null
  output_drive_file_id: string | null
  failure_reason: string | null
  filters: Record<string, unknown>
  created_at: string
  started_at: string | null
  completed_at: string | null
}

export interface CatalogExportRunsResponse {
  runs: CatalogExportRun[]
  total: number
}

export interface CatalogExportDownload {
  download_url: string
  filename: string
  expires_in_seconds: number
}

export interface BrandProfile {
  id: string
  name: string
  company_name: string
  tagline: string | null
  brand_color: string
  ink_color: string
  font_family: string
  wordmark_text: string
  contact_email: string | null
  website_url: string | null
  channels: string[]
  is_default: boolean
}

export type BrandProfileUpdate = Partial<
  Pick<
    BrandProfile,
    | 'name'
    | 'company_name'
    | 'tagline'
    | 'brand_color'
    | 'ink_color'
    | 'font_family'
    | 'wordmark_text'
    | 'contact_email'
    | 'website_url'
    | 'channels'
  >
>

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: unknown
    try {
      detail = (await res.json()).detail
    } catch {
      detail = res.statusText
    }
    const msg =
      typeof detail === 'object' && detail && 'message' in detail
        ? String((detail as { message: unknown }).message)
        : String(detail)
    throw new Error(msg)
  }
  return (await res.json()) as T
}

const BASE = '/admin/document-studio'

export function useExportRuns(options?: {
  poll?: boolean
}): UseQueryResult<CatalogExportRunsResponse> {
  return useQuery<CatalogExportRunsResponse>({
    queryKey: ['document-studio', 'exports'],
    queryFn: async () =>
      jsonOrThrow(await fetch(apiUrl(`${BASE}/exports`))),
    // Poll while any run is still queued/running so the FE reflects completion.
    refetchInterval: (query) => {
      if (!options?.poll) return false
      const data = query.state.data as CatalogExportRunsResponse | undefined
      const pending = data?.runs.some(
        (r) => r.status === 'queued' || r.status === 'running',
      )
      return pending ? 1500 : false
    },
  })
}

export function useCreateExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CatalogExportCreate) =>
      jsonOrThrow<CatalogExportRun>(
        await fetch(apiUrl(`${BASE}/exports`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['document-studio', 'exports'] }),
  })
}

export async function fetchDownloadUrl(
  runId: string,
): Promise<CatalogExportDownload> {
  return jsonOrThrow(await fetch(apiUrl(`${BASE}/exports/${runId}/download`)))
}

export async function fetchPreview(
  body: CatalogExportCreate,
): Promise<{ html: string }> {
  return jsonOrThrow(
    await fetch(apiUrl(`${BASE}/preview`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  )
}

export function useBrandProfile(): UseQueryResult<BrandProfile> {
  return useQuery<BrandProfile>({
    queryKey: ['document-studio', 'brand'],
    queryFn: async () => jsonOrThrow(await fetch(apiUrl(`${BASE}/brand`))),
  })
}

export function useUpdateBrand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: BrandProfileUpdate) =>
      jsonOrThrow<BrandProfile>(
        await fetch(apiUrl(`${BASE}/brand`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['document-studio', 'brand'] }),
  })
}
